<template>
  <v-app>
    <v-main>
      <router-view v-slot="{ Component }">
        <transition :name="transitionName" mode="out-in">
          <div
            class="page-container"
            :key="layoutShellKey"
          >
            <Suspense>
              <component :is="Component" />
            </Suspense>
          </div>
        </transition>
      </router-view>
    </v-main>
    <PWAUpdate />
  </v-app>
</template>

<script lang="ts" setup>
import { useAddressesStore, useTokenStore } from '@authvia-v3/pinia-stores-root';
import type { Address } from '@authvia-v3/pinia-stores-user';
import { useLocalStorage } from '@vueuse/core';
import { useAppStore } from '@/store/appStore';
import { useRoute, useRouter } from 'vue-router';
import { computed, type ComputedRef, Ref, ref, watch } from 'vue';
import PWAUpdate from '@/components/PWAUpdate.vue';
import '@/assets/transitions.css';
import { LocalAuthHandler } from '@/utils/LocalAuthHandler';
import { syncPiniaApiPathPrefix } from '@/utils/syncPiniaApiPathPrefix';
const route = useRoute();
const router = useRouter();
const tokenStore = useTokenStore();
const transitionName: Ref<string> = ref<string>('slide-right');

/** Keep the default layout mounted across in-app navigations; only swap inner router-views. */
const layoutShellKey: ComputedRef<string> = computed(
  () => route.matched[0]?.path ?? route.path,
);

// Watch for route changes to determine transition direction
watch(
  () => route.path,
  (to, from) => {
    if (!from) return;
    const toDepth: number = to.split('/').length;
    const fromDepth: number = from.split('/').length;
    transitionName.value = toDepth < fromDepth ? 'slide-right' : 'slide-left';
  },
);


// Watch for login route access to capture address query parameter
watch(() => route.name, (routeName) => {
  if (routeName === 'login' && route.query.address) {
    LocalAuthHandler.setAddressFromQuery(route.query.address as string);
    // Clean the URL by removing the address query parameter
    router.replace({ name: 'login' });
  }
}, { immediate: true });

/**
 * Check local storage to see if a token has been saved there, and if one has
 * then set it on the tokenSTore so that as the application loads it does so
 * in the context of the valid token in storage.
 */
const appStore = useAppStore();
const addressStore = useAddressesStore();
const localAddress = useLocalStorage(
  'AUTHVIA_LOCK_ADDRESS',
  null as null | string,
);

appStore.initializeApp(route);

watch(
  () => tokenStore.token,
  (token) => {
    syncPiniaApiPathPrefix(tokenStore.parse, token);
  },
  { immediate: true },
);

if (appStore.isAuthenticated) {
  // Prefer a fresh localStorage read so a prior VueUse instance cannot leave a stale value
  const persistedAddress: Address | undefined = LocalAuthHandler.getLockAddress();
  if (persistedAddress) {
    localAddress.value = JSON.stringify(persistedAddress);
    addressStore.setActiveAddress(persistedAddress);
  } else if (localAddress.value) {
    // VueUse may still hold a raw string when localStorage JSON is corrupt;
    // skip hydration rather than throwing during setup and blocking boot.
    try {
      addressStore.setActiveAddress(JSON.parse(localAddress.value) as Address);
    } catch {
      localAddress.value = null;
    }
  }
}
</script>
<style>
:root {
  /**
   * Font tokens — clamp scale aligned with Lock typography.css:
   *   h1 clamp(1.5rem, 5vw, 2rem) … body1 clamp(0.95rem, 2vw, 1rem) …
   * Consumed by @authvia-v3 actions via var(--action-*-font-size).
   */
  /** Merchant — ~text-h3 */
  --merchant-title-font-size: clamp(1.1rem, 4vw, 1.5rem);
  --merchant-title-font-weight: 700;
  --merchant-title-spacing: -0.0125em;
  --merchant-title-opacity: 1;
  --merchant-border-rounding: 0px;
  /** Links / inline text on white — JS sets a darker mix for light brands only */
  --merchant-text-on-light: var(--merchant-color);
  /** Action Text Color */
  --action-text-color: #3f3f3f;
  --action-text-color-light: #7a7a7a;
  --action-border-color: #868484;
  /** Action Font */
  /* --action-font: 'Poppins', sans-serif; */
  /** Action Title — ~text-h4 */
  --action-title-font-size: clamp(1rem, 3.5vw, 1.25rem);
  --action-title-font-weight: 500;
  --action-title-spacing: -0.0125em;
  --action-title-opacity: 1;
  /** Action Description — ~text-body-1 */
  --action-description-font-size: clamp(0.95rem, 2vw, 1rem);
  --action-description-font-weight: 400;
  --action-description-spacing: 0.0178571429em;
  --action-description-opacity: 0.8;
  --action-description-max-height: 8rem;
  /** Action Sub-title — ~text-subtitle-1 / Caption — ~text-body-2 */
  --action-sub-title-font-size: clamp(0.95rem, 2.5vw, 1rem);
  --action-caption-font-size: clamp(0.85rem, 1.8vw, 0.875rem);
  /** Action Button — contrast label follows merchant theme when set in Lock */
  --action-btn-text-color: var(--merchant-on-color, #ffffff);
  --action-btn-text-font-size: clamp(0.95rem, 2vw, 1rem);
  --action-btn-text-spacing: 0.0892857143em;
  --action-btn-disabled-color: #d7d7d7;
  --action-btn-disabled-text-color: #1a1a1a;

  --item-color-failed-lightest: #eedcdc;
  --item-theme-color: var(--merchant-color, #4CAF50);
  --item-theme-color-light: #6b69fc80; /** 50% opacity */
  --item-theme-color-lightest: #6b69fc0f;
  --item-color-success-lightest: #dceedf;
  --item-text-color: #3f3f3f;
  --item-text-color-light: #7a7a7a;
  --item-font: Poppins, sans-serif;

  /** Web Component Theme Color */

  /** Web Component Text Color */
  --web-component-text-color: #3f3f3f;
  --web-component-text-color-light: #747474cc;
  --web-component-border-color: #868484;
  /** Web Component Font */
  --web-component-font: Roboto, sans-serif;
  /** Web Component Title — ~text-subtitle-1; heading — ~text-h1 */
  --web-component-title-font-size: clamp(0.95rem, 2.5vw, 1rem);
  --web-component-title-font-weight: 400;
  --web-component-subtitle-font-weight: 500;
  --web-component-heading-size: clamp(1.5rem, 5vw, 2rem);
  --web-component-title-spacing: -0.0125em;
  --web-component-title-opacity: 1;
  /** Web Component Description — ~text-body-2 */
  --web-component-description-font-size: clamp(0.85rem, 1.8vw, 0.875rem);
  --web-component-description-font-weight: 400;
  --web-component-description-spacing: 0.0178571429em;
  --web-component-description-opacity: 0.8;
  /** Web Component Caption — ~text-caption */
  --av-lock-caption-font-size: clamp(0.75rem, 1.5vw, 0.75rem);
  --web-component-caption-font-size: var(--av-lock-caption-font-size);
  --web-component-caption-font-weight: 400;
  /** Web Component Button — contrast label follows merchant theme when set in Lock */
  --web-component-btn-text-color: var(--merchant-on-color, #ffffff);
  --web-component-btn-text-color-light: #7474749c;
  --web-component-btn-text-font-size: clamp(0.85rem, 1.8vw, 0.875rem);
  --web-component-btn-text-spacing: 0.0892857143em;
  --web-component-btn-disabled-color: #d7d7d7;
  --web-component-btn-height: 2.25rem;
}
</style>
